import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchRetiredPriests } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Church, Heart, Phone, Quote } from 'lucide-react';

interface RetiredPriest {
  id: number;
  name: string;
  title: string;
  photo?: string;
  phone?: string;
  retirementYear?: number;
  biography?: string;
  parishes: Array<{ name: string; location?: string }>;
  deceased: number; // API returns 1 or 0
  retired: number;  // API returns 1 or 0
}

const RetiredPriests = () => {
  const [retiredPriests, setRetiredPriests] = useState<RetiredPriest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRetiredPriests = async () => {
      try {
        const response = await fetchRetiredPriests();
        setRetiredPriests(response.data.priests);
      } catch (error) {
        console.error('Error fetching retired priests:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRetiredPriests();
  }, []);

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
       <div className="animate-pulse text-gray-500 font-serif italic text-xl">Loading Roll of Honour...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-6xl font-serif font-bold text-slate-900 mb-4">
            Roll of Honour & Memorial
          </h1>
          <div className="h-1 w-24 bg-amber-600 mx-auto mb-6"></div>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto font-light italic">
            Celebrating the legacy of those who faithfully served the Diocese of Ibadan South.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {retiredPriests.map((priest) => {
            const isDeceased = priest.deceased === 1;
            // Access the first parish in the array if it exists
            const activeParish = priest.parishes?.[0];

            return (
              <Card 
                key={priest.id} 
                className={`relative overflow-hidden transition-all duration-500 border-none shadow-sm ${
                  isDeceased 
                    ? 'bg-slate-100/80 grayscale-[0.8] hover:grayscale-0' 
                    : 'bg-white hover:shadow-xl ring-1 ring-amber-100'
                }`}
              >
                {/* Decorative Side Bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                  isDeceased ? 'bg-slate-400' : 'bg-amber-600'
                }`} />

                <CardHeader className="flex flex-row items-center gap-6 pt-8">
                  <div className="relative shrink-0">
                    <div className={`w-24 h-24 rounded-full overflow-hidden border-2 ${
                      isDeceased ? 'border-slate-300' : 'border-amber-200'
                    }`}>
                      <img 
                        src={priest.photo || "https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png"} 
                        alt={priest.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {isDeceased && (
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                        <Heart className="h-4 w-4 text-slate-400 fill-slate-400" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className={`text-xs uppercase font-bold tracking-widest mb-1 ${
                      isDeceased ? 'text-slate-500' : 'text-amber-700'
                    }`}>
                      {isDeceased ? 'Of Blessed Memory' : 'Retired Clergy'}
                    </div>
                    <CardTitle className={`text-xl md:text-2xl font-serif leading-tight ${
                      isDeceased ? 'text-slate-800' : 'text-slate-900'
                    }`}>
                      {priest.title} {priest.name}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pb-8">
                  {/* Parish Info - Conditional logic to check for the name */}
                  <div className="flex items-start gap-3 mt-2">
                    <Church className={`h-5 w-5 mt-0.5 ${isDeceased ? 'text-slate-400' : 'text-amber-600'}`} />
                    <div>
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {isDeceased ? 'Formerly Served At' : 'Last Serving Post'}
                      </span>
                      <p className="text-slate-700 font-medium">
                        {activeParish?.name || "Multiple Assignments"}
                      </p>
                      {activeParish?.location && (
                        <p className="text-sm text-slate-500">{activeParish.location}</p>
                      )}
                    </div>
                  </div>

                  {priest.biography && (
                    <div className="relative group">
                      <Quote className="absolute -top-2 -left-2 h-4 w-4 text-gray-200" />
                      <p className="text-sm text-slate-600 leading-relaxed pl-4 border-l border-slate-200 italic">
                        {priest.biography.substring(0, 160)}...
                      </p>
                    </div>
                  )}

                  <div className={`pt-4 border-t flex items-center justify-between ${
                    isDeceased ? 'border-slate-200' : 'border-amber-50'
                  }`}>
                    {!isDeceased && priest.phone && (
                      <a href={`tel:${priest.phone}`} className="flex items-center text-sm font-semibold text-amber-700 hover:text-amber-800 transition-colors">
                        <Phone className="h-4 w-4 mr-2" />
                        {priest.phone}
                      </a>
                    )}
                    
                    {isDeceased && (
                      <span className="text-sm font-serif italic text-slate-500">
                        Forever in our prayers
                      </span>
                    )}

                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${
                      isDeceased ? 'bg-slate-200 text-slate-600' : 'bg-amber-100 text-amber-800'
                    }`}>
                      ID: #{priest.id}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RetiredPriests;